import { Icon } from "../components/Icon";
import { COACH_SEES_ENGINE } from "../lib/coachEngine";
import { COACH_ENGINE_FORM } from "../lib/coachEngine";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { EngineQuestions, useEngineAnswers } from "../components/EngineQuestions";
import { PROGRESS_VIEWS, offersOneRMSwitch, tickableViews } from "../lib/progressViews";
import { BODY_IS_LOAD_HINT, suggestsBodyIsLoad } from "../lib/oneRepMax";
import { canToggleView, tickedViews, toggleView } from "../lib/progressViewPicks";
import { vocabularyLabel } from "../lib/vocabulary";
import { engineReadsMuscle } from "../lib/muscleVocabulary";
import { pastedLinkNote } from "../lib/videoThumb";
import { supabase } from "../lib/supabase";
import { DiscardWindow } from "../components/DiscardWindow";
import { PieceIcon } from "../components/EquipmentIcon";
import {
  customOfferedInBuilder, equipmentName, hiddenKeys, isCustomKey, offeredInBuilder, originalName,
  type EquipmentSetting,
} from "../lib/equipmentLibrary";
import { backdropRoute, href, type Route } from "../lib/route";
import { closeAttempt } from "../lib/builder";
import {
  CATEGORY_LABEL,
  CATEGORY_ORDER,
  CATEGORY_SUBTITLE,
  CATEGORY_TRACKING,
  EMPTY_DRAFT,
  MAX_PRIMARY_MUSCLES,
  MOVEMENT_PATTERNS,
  TRACKING_FIELD_LABEL,
  VIDEO_BUCKET,
  VIDEO_SOURCE_COACH_LINK,
  VIDEO_SOURCE_COACH_UPLOAD,
  type Category,
  type ExerciseDraft,
  type ExerciseRow,
  type MovementPattern,
  type TrackingField,
  draftFromRow,
  draftIsDirty,
  linesToText,
  rowFromDraft,
  textToLines,
  validateDraft,
  videoKind,
  videoObjectPath,
  videoRefusal,
  videoStem,
  acceptTrackingFields, addTrackingField, addableTrackingFields, dropGap, dropTrackingField, exerciseCloseStake, moveTrackingField,
  removeTrackingField,
  backspaceStepStart, pasteSteps, splitStep, type StepCaret,
} from "../lib/coachExercise";
import { BOTH_COUNTS_LINE, holdsBothCounts } from "../lib/trackingFieldRule";
import { CANONICAL_MUSCLES } from "../lib/muscles";
import { EQUIPMENT_GROUPS, EQUIPMENT_LABEL } from "../lib/equipment";
import { progressLabel, shrinkVideo, uploadResumable, type VideoProgress } from "../lib/videoUpload";
import { DIFFICULTIES, saveStatus } from "../lib/exerciseAutosave";
import { PhoneExercisePreview } from "../components/PhoneExercisePreview";
import { copyDepsFrom, copyExercise as copyExerciseRow } from "../lib/copyExercise";
import { editorLink, linkColumns, type Link } from "../lib/catalogLink";
import { CATALOG_ROWS } from "../lib/catalogLink.generated";
import { EXERCISE_NAME_MAX, acceptExerciseName, exerciseNameCounter } from "../lib/workoutNameCap";

/* ⚠️ W72 STEP 3 (2026-09-25, his yes to Proposal 3): REDRAWN AS OURS. The head is the NAME · what the autosave
 * did · Copy · Done, and the window saves as you type (`exerciseAutosave`). The form is rows on the ground —
 * Trains (tag field, ★ primary) · Movement · Kind · Logged as (its order IS the client's logging order) ·
 * Progress views · Equipment · How to do it · Difficulty · Notes — and the right column is the video and the
 * exercise ON THE CLIENT'S PHONE (`PhoneExercisePreview`, ported from the player). The data paths below —
 * load, validate, the upload, the write struct — are unchanged. The history this window carried:
 *
 * THE EXERCISE EDITOR — a window over the shelf (W1, 2026-09-18), REBUILT AS THEIR EXERCISE BUILDER
 * (2026-09-23, his word: *"copy perfectly everfit's exercise builder … hover over things too and
 * then also click"*). Read on his account at every depth before a line of this was written —
 * first view, hover, click, drag — and recorded in `docs/EVERFIT-MAP.md` §8f-copy.
 *
 * THEIR SHAPE, IN OUR TOKENS: a two-pane window. LEFT, on the elevated surface: the NAME as a large
 * inline field at the top (it stays put while the form scrolls under it), then `PRIMARY FOCUS`
 * (muscle group · movement pattern, each a closed control that opens a panel of pills), `CATEGORY`
 * in a tinted well whose choice fills the numbered, draggable TRACKING FIELDS, `INSTRUCTIONS`, and a
 * collapsed `Advanced Settings`. RIGHT, on the tinted surface and never scrolled to: `Media`. A thin
 * footer with `Save` and `Save & Close`, and a round ✕ hanging off the top-right corner.
 *
 * ⚠️ IT IS A WINDOW FOR THE SAME REASON THE BUILDER IS: it holds unsaved work, the coach edits it
 * AGAINST the shelf behind it, and `#/exercises/<id>` still MEANS the same thing. `backdropRoute`
 * declares that relationship once. ⚠️ AND THE DIRTY GUARD IS `closeAttempt`, IMPORTED — Escape may
 * not discard a draft, and a second copy of that rule here would drift from the first.
 *
 * ⚠️ WHAT THEIRS HAS AND OURS DOES NOT STORE IS LEFT OUT, NOT FAKED: modality, `Add Link`, the
 * share/ownership control, default alternates, synonyms, tags and photos have no column. A control
 * that looks like it saves and does not is worse than its absence; each is filed in `EVERFIT-MAP`
 * §6b for the together phase. And where OURS is stricter (one movement pattern where theirs takes
 * three — `movement_pattern` is one CHECKed value the engine buckets on) the control says so.
 */

/* 🔴 THE PICKER OFFERS THE WORDS THE COACH READS EVERYWHERE ELSE, AND STORES THE KEY THE ENGINE
 * READS (2026-09-22, found by driving it). `EQUIPMENT_LABEL` is key → label; the list a coach picks
 * from used to read `ab_wheel`, and a coach who typed the word they could SEE stored a spelling the
 * engine does not know. Every live row holds a KEY, so a chip is chosen by label and stored by key.
 * Muscles are the deliberate asymmetry: they are STORED as labels (which is why `MuscleNormalizer`
 * copes with 39 spellings at read time), so the displayed word already IS the stored word. */
/** Their panel is GROUPED, and so is the phone's picker — `EQUIPMENT_GROUPS` is generated from the
 *  phone's own enum (its groups, its order, its retired and admin-only pieces left out). A value a
 *  row holds that no group offers is drawn under `Other`, so opening an old row never drops it. */
function equipmentSections(held: readonly string[], query: string,
                           hidden: ReadonlySet<string> = new Set(),
                           settings: readonly EquipmentSetting[] = []): { name: string; keys: string[] }[] {
  const q = query.trim().toLowerCase();
  // A piece the coach HID on the Equipment page is not offered — unless this exercise holds it.
  // Searched by HIS name and the app's, so a renamed piece is found by either.
  const matches = (k: string) => offeredInBuilder(k, hidden, held)
    && (q === "" || equipmentName(k, settings).toLowerCase().includes(q) || originalName(k).toLowerCase().includes(q));
  const grouped = new Set(EQUIPMENT_GROUPS.flatMap((g) => g.keys));
  // W62 step C: his OWN pieces first, as the Equipment page lists them.
  const own = customOfferedInBuilder(settings, held).filter(matches);
  const sections = [{ name: "Your equipment", keys: own },
    ...EQUIPMENT_GROUPS.map((g) => ({ name: g.name, keys: g.keys.filter(matches) }))];
  const other = withStragglers([], held).filter((k) => !grouped.has(k) && !isCustomKey(k) && matches(k));
  if (other.length > 0) sections.push({ name: "Other", keys: other });
  return sections.filter((s) => s.keys.length > 0);
}
/* A→Z, as their muscle panel lists them. */
const MUSCLE_OPTIONS: readonly string[] = CANONICAL_MUSCLES.map((m) => m.label)
  .slice().sort((a, b) => a.localeCompare(b));

/* 🔴 A PANEL THAT OPENS NEAR THE FOOT OF THE FORM WAS CUT IN HALF BY THE FOOTER (rendered
   2026-09-23: the tracking-field menu's last row sat under `Save`). Theirs overflows its window;
   ours is inside a scrolling pane, so on opening it is scrolled into view instead. MODULE-LEVEL so
   the ref is one stable function — React calls it once when the panel mounts, never per render,
   which would drag the pane back on every keystroke in the equipment search. */
const revealOnOpen = (el: HTMLElement | null) => { el?.scrollIntoView({ block: "nearest" }); };

/** A stored value that is not in the offered list still shows — as a chosen pill — so opening an
 *  old row never silently drops what it holds. Offered first, then the stragglers. */
function withStragglers(options: readonly string[], values: readonly string[]): string[] {
  const lower = new Set(options.map((o) => o.toLowerCase()));
  return [...options, ...values.filter((v) => !lower.has(v.toLowerCase()))];
}

/** Which of the editor's pop-overs is open. One at a time, as theirs: opening one closes another. */
type Pop = "muscles" | "pattern" | "category" | "tracking" | "equipment" | null;

function Chevron() {
  return (
    <Icon name="chevron-down" className="xb-chevron" size={12} stroke={2} />
  );
}

/** The hostname, for the link's own face. Unparseable falls back to the raw string rather than
 *  to nothing: a coach can still see what they pasted. */
function linkHost(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
}

type Props = {
  /** `null` for a new exercise. */
  exerciseId: string | null;
  /** This window's own address — what `backdropRoute` reads to know where closing GOES. */
  route: Route;
  onSaved: () => void;
};

export function ExerciseEditor({ exerciseId, route, onSaved }: Props) {
  const [draft, setDraft] = useState<ExerciseDraft>(EMPTY_DRAFT);
  const [saved, setSaved] = useState<ExerciseDraft>(EMPTY_DRAFT);
  /* Job 3 · William: the `Logging and progress` fold follows the coach's own toggle; it is FORCED open while nothing is logged
     (the R19 finding), and a toggle the browser fires for that forced state is not the coach's, so it is not remembered. */
  const [loggingOpen, setLoggingOpen] = useState(false);
  /** The draft the window OPENED on, and where its video came from — what the ✕'s `Discard changes` puts back. */
  const [opened, setOpened] = useState<{ draft: ExerciseDraft; videoSource: string | null }>({ draft: EMPTY_DRAFT, videoSource: null });
  const [loading, setLoading] = useState(exerciseId !== null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState<"stay" | "close" | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [showProblems, setShowProblems] = useState(false);
  const [upload, setUpload] = useState<{ busy: boolean; refusal: string | null }>({
    busy: false, refusal: null,
  });
  /** Which pass the ring is on and how far through it — null when nothing is moving. */
  const [videoProgress, setVideoProgress] = useState<VideoProgress | null>(null);
  /* Where the current video came from, so a save carries it. ⚠️ NOT part of `ExerciseDraft`: the
     draft is what the coach TYPES, and provenance is a fact about the file rather than a field
     anybody edits. Null means "unchanged" — an edit that never touches the video must not restamp
     a row somebody else's pass stamped correctly. */
  const [videoSource, setVideoSource] = useState<string | null>(null);
  const [pastedLink, setPastedLink] = useState("");
  /* ⚠️ REPLACING IS NOT REMOVING-THEN-ADDING, AND THE DIFFERENCE IS THE COACH'S DATA. Before this
     the only way to change a video was "Remove video", which writes `null` into the draft — so a
     coach who then changed their mind, or whose upload failed, had already lost the URL and had
     no way back to it. This shows the two inputs while the stored video STAYS the value, and the
     old URL is only overwritten when a new one actually lands. */
  const [replacing, setReplacing] = useState(false);
  /** R17's sentence about the link just pasted — `null` when there is nothing to say. */
  const [linkNote, setLinkNote] = useState<string | null>(null);
  /* 🔴 W72 STEP 3 — SAVED AS YOU TYPE (Proposal 3's head: `Saved · 00:43 · Copy · Done`). `ownId` is the row this
     window writes: the prop for an existing exercise, and the id the FIRST autosave created for a new one — held
     here, with the address replaced in place (`replaceState` fires no `hashchange`), so the window neither
     remounts under the coach's caret nor inserts a second row on the next keystroke. */
  const [ownId, setOwnId] = useState<string | null>(exerciseId);
  /* 🔴 F15 STEP 2 — BORN LINKED: what the ROW holds of its catalogue link, as of its last load or save (name + link). Every
     save writes the pair through `editorLink` — a decided link kept while the name stands, tier 1/2 otherwise — so a web
     save links exactly as a phone save does, and never leaves a machine link describing a name the row no longer has. */
  const [stored, setStored] = useState<{ name: string | null; link: Link | null }>({ name: null, link: null });
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  /* 🔴 F15 STEP 3 — THE THREE QUESTIONS (`EngineQuestions`): their state is declared HERE, above every early return. The
     link question 1 follows is the one a save would write (`editorLink`); a tier-3 "Yes" becomes the coach's own link. */
  const heldLink = useMemo(() => editorLink(draft.name, stored.name, stored.link, CATALOG_ROWS), [draft.name, stored]);
  const engine = useEngineAnswers(ownId, draft.name, heldLink, COACH_ENGINE_FORM);
  /* A tier-3 "Yes" IS the coach's decision: it becomes the held link (source `coach`, which every save keeps while the name
     stands — `linkAtSave`), so the save's one `editorLink` line writes it and question 1 now reads it as answered. */
  const yesTo = engine.said === "yes" && engine.question.kind === "ask" ? engine.question.id : null;
  useEffect(() => {
    if (yesTo !== null) setStored((st) => ({ ...st, name: draft.name.trim(), link: { id: yesTo, source: "coach" } }));
  }, [yesTo, draft.name]);

  /** A pasted link becomes the demo video, with its own provenance.
   *
   * ⚠️ Validated only as far as "is this a URL at all" — the coach knows what they pasted, and a
   * domain allow-list here would refuse a legitimate host tomorrow. What it must NOT do is store a
   * fragment that no player and no link can use. */
  function applyPastedLink() {
    const raw = pastedLink.trim();
    if (raw === "") return;
    try { new URL(raw); } catch {
      setUpload({ busy: false, refusal: "That does not look like a link — paste the whole address." });
      return;
    }
    setUpload({ busy: false, refusal: null });
    /* 🔴 R17 — SAID AT THE PASTE, WHILE THEY CAN STILL DO SOMETHING ABOUT IT. A direct media file
       on another origin is refused by this page's `media-src` and would sit there as a player
       that never starts, with nothing on screen and nothing in a console a coach reads. The link
       is STORED exactly as typed and the exercise is fine — it is offered as a link instead, and
       the sentence says so and says what would play in the page. The rule is
       `videoThumb.pastedLinkNote`, so the note and the tile cannot disagree. */
    setLinkNote(pastedLinkNote(raw, window.location.origin));
    setDraft((d) => ({ ...d, demoVideoUrl: raw }));
    // A new link has landed, so the replace is over and the stored video is the new one.
    setReplacing(false);
    setVideoSource(VIDEO_SOURCE_COACH_LINK);
    setPastedLink("");
  }

  const dialogRef = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);

  const dirty = draftIsDirty(draft, saved) || (COACH_ENGINE_FORM && engine.dirty);
  const problems = validateDraft(draft);

  /* Navigation IS the close — nothing listens to the `close` event, or the window would have two
     sources of truth about whether it is open. `showModal()` on an already-open dialog THROWS, so
     it is guarded rather than assumed. Both from `WorkoutBuilder`, whose comments carry the
     measurements behind them. */
  useEffect(() => {
    const el = dialogRef.current;
    if (el !== null && !el.open) el.showModal();
    heading.current?.focus();
    return () => { if (el !== null && el.open) el.close(); };
  }, []);

  useEffect(() => {
    if (exerciseId === null) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("coach_exercises")
        .select("id, name, movement_pattern, equipment, primary_muscles, tracking_fields, progress_views, one_rm_enabled, body_is_load, "
                + "category, difficulty, instructions, tips, notes, demo_video_url, demo_video_source, updated_at, "
                + "catalog_exercise_id, catalog_link_source")
        .eq("id", exerciseId)
        .maybeSingle();
      if (cancelled) return;
      setLoading(false);
      if (error !== null) { setLoadError(error.message); return; }
      // ⚠️ A ROW THAT IS NOT THERE IS ITS OWN ANSWER, never an empty form. An editor that opens
      // blank on a deleted or mistyped id invites a coach to fill it in and create a SECOND
      // exercise while believing they are editing the first.
      if (data === null) {
        setLoadError("That exercise no longer exists. It may have been deleted.");
        return;
      }
      const d = draftFromRow(data as unknown as ExerciseRow);
      setDraft(d);
      setSaved(d);
      setOpened({ draft: d, videoSource: (data as unknown as { demo_video_source: string | null }).demo_video_source ?? null });
      const held = data as unknown as { name: string; catalog_exercise_id: string | null; catalog_link_source: string | null };
      setStored({
        name: held.name,
        link: held.catalog_exercise_id !== null && held.catalog_link_source !== null && held.catalog_link_source !== ""
          ? { id: held.catalog_exercise_id, source: held.catalog_link_source } : null,
      });
      // The head says WHEN it was last saved from the first frame — the trigger keeps the column true for every writer.
      const at = (data as unknown as ExerciseRow).updated_at;
      if (at !== null && at !== undefined) setSavedAt(new Date(at));
    })();
    return () => { cancelled = true; };
  }, [exerciseId]);

  function goBack() {
    window.location.hash = href(backdropRoute(route) ?? { name: "exercises" });
  }

  /* 🔴 THE ✕ (his word, 2026-09-28) — the SAME `closeAttempt` the workout builder asks, with this window's stake: a blank
     or unchanged exercise closes without a word; anything changed since it opened asks Keep · Discard changes. */
  const createdHere = exerciseId === null;
  function requestClose() {
    const stake = exerciseCloseStake(draft, opened.draft, createdHere);
    switch (closeAttempt({ dirty, saving: saving !== null, confirming, stake })) {
      case "wait": return;
      case "dismissConfirm": setConfirming(false); return;
      case "confirm": setConfirming(true); return;
      case "close": void closeQuietly(stake.exercises === 0); return;
    }
  }

  /** Nothing asked: nothing changed since open (W95: nothing is written before Save), so just leave — a blank NEW
   *  exercise with a row (only a Save that stayed makes one) is removed. */
  async function closeQuietly(blank: boolean) {
    if (blank && createdHere && ownId !== null) { await removeCreated(); return; }
    goBack();
  }

  /** `Save` (the question's safe answer) — write, then leave; an invalid change stays, showing what to fix. */
  async function keepAndLeave() {
    setConfirming(false);
    if (dirty && problems.length > 0) { setShowProblems(true); return; }
    if (dirty) { await save("close"); return; }
    goBack();
  }

  /** `Discard changes` — W95: nothing was written before Save, so there is nothing to put back. A new exercise with a row
   *  (a Save that stayed) is removed; otherwise the window just leaves. No write. */
  async function discardChanges() {
    setConfirming(false);
    if (createdHere && ownId !== null) { await removeCreated(); return; }
    goBack();
  }

  /** Remove the row an autosave created in this window — only while no workout uses it (it can be used from another tab). */
  async function removeCreated() {
    if (ownId === null) { goBack(); return; }
    const { count, error: countError } = await supabase.from("coach_workout_exercises")
      .select("id", { count: "exact", head: true }).eq("exercise_id", ownId);
    if (countError !== null || count === null) { setSaveError(countError?.message ?? "Could not check whether a workout uses it."); return; }
    if (count > 0) { setSaveError("A workout already uses this exercise, so it was kept."); return; }
    const { error } = await supabase.from("coach_exercises").delete().eq("id", ownId);
    if (error !== null) { setSaveError(error.message); return; }
    onSaved();
    goBack();
  }

  function onCancel(e: React.SyntheticEvent<HTMLDialogElement>) {
    // `cancel` is cancelable precisely so Escape cannot discard a draft.
    e.preventDefault();
    requestClose();
  }

  /* TWO VERBS, THEIRS: `Save` keeps the window open on the saved row, `Save & Close` returns to the
     shelf. A new exercise saved with `Save` moves to its own address, so a reload reopens it. */
  async function save(mode: "stay" | "close") {
    if (problems.length > 0) { setShowProblems(true); return; }
    setSaving(mode);
    setSaveError(null);
    // ⚠️ The provenance rides WITH the url or not at all: a row whose video this session did not
    // touch keeps whatever it had, and a row whose video was REMOVED loses its provenance too,
    // because a source for a video that is gone is a fact about nothing.
    const link = editorLink(draft.name, stored.name, stored.link, CATALOG_ROWS);
    const row = {
      ...rowFromDraft(draft),
      ...linkColumns(link),
      ...(draft.demoVideoUrl === null
        ? { demo_video_source: null }
        : videoSource !== null ? { demo_video_source: videoSource } : {}),
    };

    if (ownId === null) {
      // 🔴 **`coach_id` HAS NO DEFAULT AND IS NOT NULL — MEASURED, after I had written the
      // opposite in this very comment.** The first draft said the column defaults to `auth.uid()`
      // so sending it would be a second statement of who the owner is; `information_schema` says
      // `column_default = NULL, is_nullable = NO`, and the insert would simply have failed.
      //
      // ⚠️ **WHAT ACTUALLY MAKES A WRONG VALUE IMPOSSIBLE IS THE POLICY, NOT A DEFAULT.**
      // `coach_exercises_owner_all` carries `with_check (coach_id = auth.uid())` (MEASURED from
      // `pg_policies`), so an insert naming somebody else's id is REFUSED — a coach cannot write
      // into another coach's library even by trying. The client supplies the id; the database
      // decides whether it may.
      const { data: sessionData } = await supabase.auth.getSession();
      const coachId = sessionData.session?.user.id;
      if (coachId === undefined) {
        setSaving(null);
        setSaveError("You are signed out. Sign in again and your work here is still on screen.");
        return;
      }
      const { data, error } = await supabase
        .from("coach_exercises").insert({ ...row, coach_id: coachId }).select("id").single();
      setSaving(null);
      if (error !== null) { setSaveError(error.message); return; }
      if (!(await writeEngine(data.id))) return;
      setSaved(draft);
      setSavedAt(new Date());
      setStored({ name: draft.name.trim(), link });
      setOwnId(data.id);
      onSaved();
      if (mode === "close") { goBack(); return; }
      // In place: the row now has an address a reload reopens, and nothing remounts under the caret.
      window.history.replaceState(null, "", href({ name: "exercise", exerciseId: data.id }));
      return;
    }

    const { error } = await supabase.from("coach_exercises").update(row).eq("id", ownId);
    setSaving(null);
    if (error !== null) { setSaveError(error.message); return; }
    if (!(await writeEngine(ownId))) return;
    setSaved(draft);
    setSavedAt(new Date());
    setStored({ name: draft.name.trim(), link });
    onSaved();
    if (mode === "close") goBack();
  }

  /** F15 step 3 — the engine row, written AFTER its exercise row (the FK) and only when READY: a half-answered form writes
   *  nothing here (§3.2), and its simple fields are already saved above. */
  async function writeEngine(coachExerciseId: string): Promise<boolean> {
    if (!COACH_ENGINE_FORM) return true;
    const w = engine.write(coachExerciseId);
    if (w !== null) {
      const { error } = await supabase.from("coach_exercise_engine").upsert(w, { onConflict: "coach_exercise_id" });
      if (error !== null) { setSaveError(error.message); return false; }
    }
    engine.saved();
    return true;
  }

  /* SAVE (W95 — was Done, renamed as in the workout builder) — the one write. A valid change is written and the window
     closes; an INVALID one stays and shows what to fix (never the discard question — the coach pressed Save); nothing
     changed → close. */
  async function done() {
    if (saving !== null) return;
    if (dirty && problems.length === 0) { await save("close"); return; }
    if (dirty) { setShowProblems(true); return; }
    requestClose();
  }

  /* COPY — a new row with this one's fields and `(copy)` on its name, then the window moves to it. The video
     travels with its PROVENANCE read from the row, never guessed. Only a SAVED, valid exercise can be copied. */
  async function copyExercise() {
    // W95: Copy copies the SAVED exercise — never a silent save of unsaved edits (the button is off while there are any).
    if (ownId === null || problems.length > 0 || saving !== null || dirty) return;
    const { data: sessionData } = await supabase.auth.getSession();
    const coachId = sessionData.session?.user.id;
    if (coachId === undefined) { setSaveError("You are signed out. Sign in again and your work here is still on screen."); return; }
    // ⚠️ THE ONE COPY (`lib/copyExercise.ts`), shared with the Library row's hover `Copy`: it reads the SAVED row
    // (the draft was just saved above) and writes it the way this builder writes, so the two copies cannot drift.
    const made = await copyExerciseRow(copyDepsFrom(supabase), ownId, coachId);
    if (!made.ok) { setSaveError(made.message); return; }
    onSaved();
    window.location.hash = href({ name: "exercise", exerciseId: made.id });
  }

  /* THE UPLOAD.
   *
   * ⚠️ Refused BEFORE the first byte where it can be: the bucket answers an oversized object with
   * a 413 after the whole file has crossed the wire, which on a 200 MB limit is minutes of waiting
   * for a number. ⚠️ The path lowercases the owner folder — `videoObjectPath` is the one place a
   * path is made, and the repo's five-builders trap is what that rule is for. ⚠️ `upsert: false`
   * and a fresh stem every time: the bucket is public and a replaced object has been served stale
   * from the CDN before.
   */
  async function uploadVideo(original: File) {
    // 🔴 TOO BIG IS NO LONGER A REFUSAL — IT IS A SHRINK (his word, 2026-09-23). The TYPE is still
    // refused before anything happens; an oversized mp4/mov is re-encoded in the browser to land
    // under the bucket, and `videoRefusal` then judges the file that will actually be sent.
    const typeRefusal = videoRefusal({ size: 0, type: original.type });
    if (typeRefusal !== null) { setUpload({ busy: false, refusal: typeRefusal }); return; }
    setUpload({ busy: true, refusal: null });

    const { data: sessionData } = await supabase.auth.getSession();
    const coachId = sessionData.session?.user.id;
    const token = sessionData.session?.access_token;
    if (coachId === undefined || token === undefined) {
      setUpload({ busy: false, refusal: "You are signed out. Sign in again and retry." });
      return;
    }

    let file = original;
    let path = "";
    try {
      if (videoRefusal(original) !== null) {
        setVideoProgress({ phase: "shrinking", fraction: 0, fromBytes: original.size });
        file = await shrinkVideo(original, (fraction) =>
          setVideoProgress({ phase: "shrinking", fraction, fromBytes: original.size }));
      }
      const refusal = videoRefusal(file);
      if (refusal !== null) throw new Error(refusal);

      const extension = file.type === "video/quicktime" ? "mov" : "mp4";
      const freshPath = () => videoObjectPath(coachId,
        videoStem(new Date(), Math.random().toString(36).slice(2, 8), extension));
      path = freshPath();
      // 🔴 THE TOKEN IS READ AFTER THE SHRINK, NOT BEFORE IT: a shrink can run for many minutes, and
      // the one read at the top of this function may have been replaced by a refresh meanwhile.
      const { data: fresh } = await supabase.auth.getSession();
      const uploadToken = fresh.session?.access_token ?? token;
      // 🔴 PROGRESS, THEN THE PROVEN PATH IF PROGRESS IS REFUSED (W62, his second round, 2026-09-23).
      // The first progress upload (one raw XHR) was refused live — *"'alg' (Algorithm) Header
      // Parameter value not allowed"*, cause NOT measured (it needs his session). The resumable
      // endpoint is Supabase's documented large-file path; if it fails for ANY reason, the same file
      // goes through the library's own upload — the call that has always worked — under a NEW name
      // (`upsert: false`, and a half-written resumable object may hold the first one). So the worst a
      // coach can now see is the ring stopping at a percentage and "Uploading…" finishing the job.
      setVideoProgress({ phase: "uploading", fraction: 0 });
      try {
        await uploadResumable(VIDEO_BUCKET, path, file, file.type, uploadToken,
          (fraction) => setVideoProgress({ phase: "uploading", fraction }));
      } catch (resumable) {
        console.warn("[upload] resumable path refused, falling back to the plain upload:", resumable);
        setVideoProgress(null);
        path = freshPath();
        // The server's own sentence on a refusal, not a paraphrase — a 42501 means the path did not
        // match the policy, and hiding that behind "upload failed" is how the lowercase bug survived.
        const { error } = await supabase.storage.from(VIDEO_BUCKET)
          .upload(path, file, { upsert: false, contentType: file.type });
        if (error !== null) throw new Error(error.message);
      }
    } catch (e) {
      setVideoProgress(null);
      setUpload({ busy: false, refusal: e instanceof Error ? e.message : "The upload failed. Try again." });
      return;
    }
    setVideoProgress(null);
    const { data: pub } = supabase.storage.from(VIDEO_BUCKET).getPublicUrl(path);
    setUpload({ busy: false, refusal: null });
    setDraft((d) => ({ ...d, demoVideoUrl: pub.publicUrl }));
    // The upload finished, so the replace is over — the player below is now the new file.
    setReplacing(false);
    // 🔴 THE PROVENANCE, written with the url and never after it. `verify.sh` counts this column
    // and a NULL reads as a clip whose origin nobody recorded.
    setVideoSource(VIDEO_SOURCE_COACH_UPLOAD);
  }

  /* ── The builder's own UI state. Declared HERE, above every early return below — a hook after a
     `return` kills the component at runtime with `tsc` and the suite green (`web-css`, W24). ── */
  const [pop, setPop] = useState<Pop>(null);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [equipmentQuery, setEquipmentQuery] = useState("");
  const [muscleQuery, setMuscleQuery] = useState("");
  /** The gap a dragged tracking card would land in, drawn as a bar — null when nothing is dragged. */
  const [dropAt, setDropAt] = useState<number | null>(null);
  const muscleInput = useRef<HTMLInputElement>(null);
  /** The pieces this coach hid on the Library's Equipment page — read once; empty until read,
   *  which offers everything, the safe direction (hiding is a convenience, never a guard). */
  const [hiddenEquipment, setHiddenEquipment] = useState<ReadonlySet<string>>(new Set());
  /** His names and icons for equipment (W62 step C) — the same rows the Equipment page writes. */
  const [equipmentSettings, setEquipmentSettings] = useState<readonly EquipmentSetting[]>([]);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // HIS rows by name: once a client may read their coach's equipment, a coach who is also
      // somebody's client would otherwise be offered that other coach's pieces.
      const { data: sessionData } = await supabase.auth.getSession();
      const me = sessionData.session?.user.id ?? "";
      const { data } = await supabase.from("coach_equipment").select("key, hidden, name, icon_path").eq("coach_id", me);
      if (!cancelled && data !== null) { setHiddenEquipment(hiddenKeys(data)); setEquipmentSettings(data); }
    })();
    return () => { cancelled = true; };
  }, []);
  const equipmentIconPath = (k: string) => equipmentSettings.find((s) => s.key === k)?.icon_path ?? null;
  const cardsRef = useRef<HTMLOListElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const problemsRef = useRef<HTMLDivElement>(null);

  /* Theirs opens a NEW exercise with the caret already in the name. An existing one keeps focus on
     the window's heading, as every window here does, so a keyboard user hears what opened. */
  useEffect(() => {
    if (exerciseId === null) nameRef.current?.focus();
  }, [exerciseId]);

  /* One pop-over at a time, and a press anywhere outside the open one closes it — theirs. */
  useEffect(() => {
    if (pop === null) return;
    const onDown = (e: PointerEvent) => {
      const anchor = (e.target as Element | null)?.closest("[data-pop-anchor]");
      if (anchor?.getAttribute("data-pop-anchor") !== pop) setPop(null);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [pop]);

  /* A refused save says why WHERE THE COACH IS LOOKING: the list sits at the end of the form, so
     it is brought into view rather than left below the fold. */
  useEffect(() => {
    if (showProblems && problems.length > 0) problemsRef.current?.scrollIntoView({ block: "nearest" });
  }, [showProblems, problems.length]);

  /* 🔴 W95 — NO AUTOSAVE (his word, 2026-09-29: "the single-exercise editor need to save like workouts"): the row is
     written only by Save. A tab closed on unsaved work is asked about by the browser's own leave prompt. */
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const title = ownId === null ? "New exercise" : (saved.name || "Exercise");
  const status = saveStatus({ saving: saving !== null, error: saveError, dirty, problems: problems.map((p) => p.message),
                              savedAt, exists: ownId !== null });

  /* ⚠️ ESCAPE INSIDE AN OPEN POP-OVER CLOSES THE POP-OVER, NOT THE WINDOW. The dialog's `cancel`
     would otherwise reach `closeAttempt`, which on a clean form closes the whole editor — a coach
     dismissing a menu would lose the window. */
  function dialogCancel(e: React.SyntheticEvent<HTMLDialogElement>) {
    if (pop !== null) { e.preventDefault(); setPop(null); return; }
    onCancel(e);
  }
  function escapeCloses(e: React.KeyboardEvent) {
    if (e.key === "Escape" && pop !== null) { e.preventDefault(); e.stopPropagation(); setPop(null); }
  }
  const toggle = (which: Exclude<Pop, null>) => setPop((p) => (p === which ? null : which));

  const frame = (body: React.ReactNode, foot?: React.ReactNode) => (
    /* ⚠️ THE QUESTION IS A SIBLING OF THIS WINDOW, NOT A CHILD — see `DiscardWindow`. */
    <>
    <dialog ref={dialogRef} className="ht-window exercise-window xb-window"
            aria-labelledby="exercise-window-title" onCancel={dialogCancel}>
      {/* 🔴 W105 — THE ✕ ON THE WINDOW'S TOP-LEFT CORNER, THREE-QUARTERS OUTSIDE (his word 2026-09-30; Fable: top-left),
          the workout builder's own `.ht-corner-close`: the dialog's FIRST child (first in the tab order), beside the frame
          (which clips). It asks the SAME `requestClose` Escape asks. It left the head, where W72 step 3 had put it. */}
      <button type="button" className="ht-corner-close" aria-label="Close" title="Close" onClick={requestClose}>
        <Icon name="x" size={18} stroke={2} />
      </button>
      <div className="ht-window-frame xb-frame">
        {/* The dialog's name and its focus target. Theirs has no title bar — the exercise's own
            name is the heading a sighted coach reads — so this one is for the screen reader. */}
        <h2 className="ht-visually-hidden" id="exercise-window-title" tabIndex={-1} ref={heading}>
          {title}
        </h2>
        {body}
        {foot}
      </div>
    </dialog>
    {/* 🔴 ONE QUESTION, ONE COMPONENT — the builder raises the same one (`EVERFIT-MAP` §8f, §12). */}
    <DiscardWindow open={confirming} onCancel={requestClose} onDiscard={() => { void discardChanges(); }}
                   keep={{
                     title: "Save your changes?",
                     label: "Save",
                     sentence: createdHere
                       ? "This new exercise has not been saved yet. Discard leaves without it."
                       : "Your changes have not been saved yet. Discard leaves the exercise as it was saved.",
                     onKeep: () => { void keepAndLeave(); },
                     busy: saving !== null,
                   }} />
    </>
  );

  if (loadError !== null) {
    return frame(
      <div className="ht-window-body xb-message">
        <p className="ht-callout exercise-error" role="alert">{loadError}</p>
      </div>,
    );
  }
  if (loading) {
    return frame(
      <div className="ht-window-body xb-message">
        <p className="ht-callout loading-line" aria-live="polite">Loading…</p>
      </div>,
    );
  }

  const set = <K extends keyof ExerciseDraft>(key: K, value: ExerciseDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  /* ── muscles: up to three, the FIRST is the primary, and their ★ is how it is chosen ── */
  const muscles = draft.primaryMuscles;
  const muscleIndex = (m: string) => muscles.findIndex((v) => v.toLowerCase() === m.toLowerCase());
  const muscleMatches = withStragglers(MUSCLE_OPTIONS, muscles).filter((m) =>
    muscleQuery.trim() === "" || vocabularyLabel(m).toLowerCase().includes(muscleQuery.trim().toLowerCase()));
  function toggleMuscle(m: string) {
    const i = muscleIndex(m);
    if (i >= 0) set("primaryMuscles", muscles.filter((_, j) => j !== i));
    else if (muscles.length < MAX_PRIMARY_MUSCLES) set("primaryMuscles", [...muscles, m]);
  }
  function makePrimary(m: string) {
    const i = muscleIndex(m);
    if (i <= 0) return;
    set("primaryMuscles", [muscles[i]!, ...muscles.filter((_, j) => j !== i)]);
  }

  /* ── category: a pick REPLACES the tracking fields with that category's own, as theirs ── */
  function chooseCategory(c: Category) {
    setDraft((d) => ({ ...d, category: c, trackingFields: [...CATEGORY_TRACKING[c]] }));
    setPop(null);
  }
  const fields = draft.trackingFields;

  /* ── equipment: chosen by label, stored by key ── */
  const equipment = draft.equipment;
  const equipmentShown = equipmentSections(equipment, equipmentQuery, hiddenEquipment, equipmentSettings);
  function toggleEquipment(k: string) {
    set("equipment", equipment.includes(k) ? equipment.filter((v) => v !== k) : [...equipment, k]);
  }

  /* ── W72 step 3: KIND is a row of choices on the ground (Proposal 3), never a pop-over; a KIND pick still REPLACES the
     logged fields with that kind's own, as it always has — `chooseCategory`. MOVEMENT (eleven, one-of, nullable) is a
     SELECT since William's first look (10-14: *"Maybe this could be a dropdown box to look cleaner?"*; Fable 10-15 weighed
     it: eleven chips over three rows against one line) — `None` clears it, as the second press used to. ── */
  const addable = addableTrackingFields(fields);
  /* W136 (his word 2026-10-02, "Reps and Time together are not making sense"; Fable's ruling): every field-list edit goes
     through the one acceptance rule — an edit that still holds both Reps and Time is refused, a stored legacy pair is kept
     as it is until the coach removes one. */
  function setFields(next: readonly string[]) {
    set("trackingFields", acceptTrackingFields(fields, next));
  }
  const offered = tickableViews(fields);
  /* ── W125: the 1RM switch. Turning it ON pre-suggests `The body is part of the load` for bar / rings / dip work — never beside a
     resistance band (W125-F1) — shown, flippable, never a silent write; OFF clears it. ── */
  function setOneRM(on: boolean) {
    setDraft((d) => ({ ...d, oneRMEnabled: on, bodyIsLoad: on ? (d.bodyIsLoad || suggestsBodyIsLoad(d.equipment)) : false }));
  }

  return frame(
    <div className="ht-window-body exercise-body xe-body">
      {/* THE HEAD: the name, large and inline; the save line; Copy (tertiary) and Save (primary) — W95, saves only on Save. */}
      <header className="xe-head">
        {/* HIS 2026-10-09 WORD — a name stops at 28 so the client's workout title stays ONE line: growth past the cap is
            refused (an older, longer name is never cut), a quiet `24/28` near it — the phone's rule, emitted (`ExerciseNameCap`). */}
        <span className="xe-name-wrap">
          <input ref={nameRef} className="xe-name" value={draft.name} placeholder="Name your exercise"
                 aria-label="Exercise name" onChange={(e) => set("name", acceptExerciseName(draft.name, e.target.value))} />
          {exerciseNameCounter(draft.name) !== null && (
            <span className="xe-name-count" aria-label={`${[...draft.name].length} of ${EXERCISE_NAME_MAX} characters`}>
              {exerciseNameCounter(draft.name)}
            </span>
          )}
        </span>
        <p className={"xe-status" + (status.tone === "warn" ? " is-warn" : "")} role="status" aria-live="polite">
          {status.text}
        </p>
        {ownId !== null && (
          <button type="button" className="ours-text-action xe-copy" onClick={() => { void copyExercise(); }}
                  disabled={saving !== null || problems.length > 0 || dirty}
                  title={dirty ? "Save your changes to copy this exercise" : undefined}>
            Copy
          </button>
        )}
        <button type="button" className="ht-button ht-button-primary xe-done" onClick={() => { void done(); }}
                disabled={saving === "close"}>
          {saving === "close" ? "Saving…" : "Save"}
        </button>
      </header>

      <div className="xe-columns">
        <div className="xe-form">
          {/* TRAINS — the muscles as a tag field that is always open; ★ on a chip makes it the primary (the FIRST is
              the primary, as the column has always meant). The pills below filter as the coach types. */}
          <section className="xe-row" aria-labelledby="xe-trains">
            <h3 className="xe-label" id="xe-trains">Trains</h3>
            <div className="xb-select xb-select-tags xb-tagfield xe-tagfield"
                 onPointerDown={(e) => {
                   if (e.target === e.currentTarget) { e.preventDefault(); muscleInput.current?.focus(); }
                 }}>
              {muscles.map((m, i) => (
                <span key={m} className="xb-tag xe-tag">
                  <button type="button" className={i === 0 ? "xb-star is-primary" : "xb-star"}
                          aria-pressed={i === 0}
                          aria-label={i === 0 ? `${m} is the primary muscle` : `Make ${m} the primary muscle`}
                          onClick={() => makePrimary(m)}>
                    <StarIcon />
                  </button>
                  {vocabularyLabel(m)}
                  {COACH_SEES_ENGINE && !engineReadsMuscle(m) && <span className="xb-flag" aria-label="Not a name the engine knows">?</span>}
                  <button type="button" className="xb-tag-x" aria-label={`Remove ${vocabularyLabel(m)}`}
                          onClick={() => { toggleMuscle(m); muscleInput.current?.focus(); }}>
                    <span aria-hidden="true">✕</span>
                  </button>
                </span>
              ))}
              {muscles.length < MAX_PRIMARY_MUSCLES && (
                <input ref={muscleInput} className="xb-tag-input xe-tag-input" value={muscleQuery}
                       placeholder={muscles.length === 0 ? "add a muscle…" : "add…"}
                       aria-label="Add a muscle" onFocus={() => setPop("muscles")}
                       onChange={(e) => { setMuscleQuery(e.target.value); setPop("muscles"); }}
                       onKeyDown={(e) => {
                         if (e.key === "Backspace" && muscleQuery === "" && muscles.length > 0) {
                           e.preventDefault();
                           set("primaryMuscles", muscles.slice(0, -1));
                         } else if (e.key === "Enter") {
                           e.preventDefault();
                           const first = muscleMatches.find((m) => muscleIndex(m) < 0);
                           if (first !== undefined) { toggleMuscle(first); setMuscleQuery(""); }
                         } else if (e.key === "Escape") {
                           e.preventDefault(); e.stopPropagation(); setPop(null);
                         }
                       }} />
              )}
            </div>
            {pop === "muscles" && muscles.length < MAX_PRIMARY_MUSCLES && (
              <div className="xe-suggest" data-pop-anchor="muscles" role="group" aria-label="Muscles">
                {muscleMatches.filter((m) => muscleIndex(m) < 0).slice(0, 14).map((m) => (
                  <button key={m} type="button" className="ours-chip" onClick={() => { toggleMuscle(m); setMuscleQuery(""); muscleInput.current?.focus(); }}>
                    {vocabularyLabel(m)}
                  </button>
                ))}
                {muscleMatches.filter((m) => muscleIndex(m) < 0).length === 0 && <p className="xe-hint">Nothing matches.</p>}
              </div>
            )}
            <p className="xe-hint">Up to {MAX_PRIMARY_MUSCLES}. The ★ marks the primary{COACH_SEES_ENGINE ? " — the engine reads these" : ""}.</p>
          </section>

          {/* William's first look (10-14): MOVEMENT's eleven chips over three rows → ONE select; KIND's five stay chips, on ONE
              row (a pick changes what is logged — every option in view), its caption under them. */}
          <section className="xe-row">
            <label className="xe-label" htmlFor="xe-movement">Movement</label>
            <select id="xe-movement" className="ht-input xe-select" value={draft.movementPattern ?? ""}
                    onChange={(e) => set("movementPattern", e.currentTarget.value === "" ? null : (e.currentTarget.value as MovementPattern))}>
              <option value="">None</option>
              {MOVEMENT_PATTERNS.map((p) => <option key={p} value={p}>{vocabularyLabel(p)}</option>)}
            </select>
          </section>
          <section className="xe-row" aria-labelledby="xe-kind">
            <h3 className="xe-label" id="xe-kind">Kind</h3>
            <div className="ours-chips xe-kind-chips" role="radiogroup" aria-labelledby="xe-kind">
              {CATEGORY_ORDER.map((c) => (
                <button key={c} type="button" role="radio" className="ours-chip" aria-checked={draft.category === c}
                        aria-pressed={draft.category === c} title={CATEGORY_SUBTITLE[c]}
                        onClick={() => chooseCategory(c)}>
                  {CATEGORY_LABEL[c]}
                </button>
              ))}
            </div>
            <p className="xe-hint">{draft.category === null ? "Picking one fills in what is logged." : CATEGORY_SUBTITLE[draft.category]}</p>
          </section>

          {/* 🔴 JOB 3 · WILLIAM (2026-10-15, "a little overwhelming with all the options"): LOGGED AS · PROGRESS VIEWS · 1RM fold
              into ONE disclosure under Kind — Kind already fills them in ("Picking one fills in what is logged"), so most
              exercises never touch them. The summary line says what is logged, so nothing is hidden that the eye needs; it
              is held open while nothing is logged (that empty state is a finding, R19). Every control inside unchanged. */}
          <details className="xe-row xe-fold" open={loggingOpen || fields.length === 0}
                   onToggle={(e) => { if (fields.length > 0) setLoggingOpen(e.currentTarget.open); }}>
            <summary className="xe-fold-summary">
              <span className="xe-label">Logging and progress</span>
              <span className="xe-fold-now">{fields.length === 0 ? "Nothing logged" : fields.map((f) => TRACKING_FIELD_LABEL[f]).join(" · ")}</span>
            </summary>
            {/* LOGGED AS — numbered, and its ORDER IS THE CLIENT'S LOGGING ORDER (his 2026-09-25 question, answered
                yes): drag a card, or use its two named buttons; the phone beside this form re-draws as it moves. */}
            <section className="xe-row" aria-labelledby="xe-logged">
              <h3 className="xe-label" id="xe-logged">Logged as</h3>
              {fields.length === 0 ? (
                /* 🔴 THE EMPTY STATE IS A FINDING, NOT A BLANK — a row that records nothing is a row the phone
                   cannot log (R19). */
                <p className="xb-tracking-empty">Nothing — a set of this saves no numbers, so a client cannot log it.</p>
              ) : (
                <ol className={"xb-cards xe-cards" + (dropAt === fields.length ? " drop-end" : "")}
                    ref={cardsRef} aria-label="What a set records, in this order"
                    onDragOver={(e) => {
                      if (dragFrom === null) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      const boxes = [...(cardsRef.current?.children ?? [])].map((c) => c.getBoundingClientRect());
                      setDropAt(dropGap(boxes, e.clientX, e.clientY));
                    }}
                    onDragLeave={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropAt(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragFrom !== null && dropAt !== null) setFields(dropTrackingField(fields, dragFrom, dropAt));
                      setDragFrom(null);
                      setDropAt(null);
                    }}>
                  {fields.map((f: TrackingField, i) => (
                    <li key={f} className={"xb-card" + (dragFrom === i ? " is-dragging" : "") + (dropAt === i ? " drop-before" : "")}
                        draggable
                        onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", f); setDragFrom(i); }}
                        onDragEnd={() => { setDragFrom(null); setDropAt(null); }}>
                      <span className="xb-card-n">{i + 1}.</span>
                      <span className="xb-card-label">{TRACKING_FIELD_LABEL[f]}</span>
                      <button type="button" className="xb-card-move" disabled={i === 0}
                              onClick={() => setFields(moveTrackingField(fields, i, -1))}>
                        Move {TRACKING_FIELD_LABEL[f]} earlier
                      </button>
                      <button type="button" className="xb-card-move" disabled={i === fields.length - 1}
                              onClick={() => setFields(moveTrackingField(fields, i, 1))}>
                        Move {TRACKING_FIELD_LABEL[f]} later
                      </button>
                      <button type="button" className="xb-card-x" aria-label={`Remove ${TRACKING_FIELD_LABEL[f]}`}
                              onClick={() => setFields(removeTrackingField(fields, f))}>
                        <span aria-hidden="true">✕</span>
                      </button>
                    </li>
                  ))}
                </ol>
              )}
              {holdsBothCounts(fields) && <p className="xe-hint xe-both" role="note">{BOTH_COUNTS_LINE}</p>}
              {/* ⚠️ GONE AT THE CAP (`array_length <= 3`), and only what is not on the list yet — his 2026-09-23 word.
                  `addableTrackingFields` is the one rule; the adds are words on the ground, never a menu. */}
              {addable.length > 0 && (
                <p className="xe-adds">
                  {addable.map((f, i) => (
                    <span key={f}>
                      {i > 0 && <span aria-hidden="true"> · </span>}
                      <button type="button" className="ours-text-action" onClick={() => setFields(addTrackingField(fields, f))}>
                        + {TRACKING_FIELD_LABEL[f]}
                      </button>
                    </span>
                  ))}
                </p>
              )}
              <p className="xe-hint">What the client types after each set, in this order — drag to reorder.</p>
            </section>

            {/* PROGRESS VIEWS — under the fields they are derived from (his 2026-09-25 word: "first set the exercise
                fields so then we have less options to choose from"). The options are the phone's own chart fields for
                what this exercise logs (GENERATED, `progressViews.ts`); all ticked by default; the last tick stays,
                because the phone would show every view anyway (`progressViewPicks`). */}
            {offered.length > 0 && (
              <fieldset className="xe-row xb-views">
                <legend className="xe-label">Progress views</legend>
                <ul className="xb-views-list">
                  {PROGRESS_VIEWS.filter((v) => tickableViews(fields).includes(v.key)).map((v) => {
                    const on = tickedViews(draft.progressViews, fields).includes(v.key);
                    return (
                      <li key={v.key}>
                        <label className="xb-view">
                          <input type="checkbox" checked={on}
                                 disabled={!canToggleView(draft.progressViews, fields, v.key)}
                                 onChange={() => set("progressViews", toggleView(draft.progressViews, fields, v.key))} />
                          <span className="xb-view-label">{v.label}</span>
                          <span className="xb-view-sub">{v.longLabel.split(" — ")[1] ?? ""}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
                {/* W124 — his question was whether a coach must ENTER a number here: nothing is typed, and the hint says so. */}
                <p className="xe-hint">Worked out from what the client logs — nothing to enter.</p>
              </fieldset>
            )}

            {/* 🔴 W125 — THE 1RM, OPT-IN (his words 2026-10-01: "we need a switch on/off for the 1RM so it won't show in the
                exercise builder by default, to keep things tidy"). Drawn only when the exercise logs weight AND reps; OFF says
                nothing more; ON reveals the body-as-load switch and its one-line hint. */}
            {offersOneRMSwitch(fields) && (
              <fieldset className="xe-row xb-onerm">
                <legend className="xe-label">1RM</legend>
                <label className="xe-switch">
                  <input type="checkbox" role="switch" className="review-mute-input" checked={draft.oneRMEnabled}
                         onChange={(e) => setOneRM(e.target.checked)} />
                  <span className="review-mute-track" aria-hidden="true" />
                  <span>Show estimated 1RM</span>
                </label>
                {draft.oneRMEnabled && (
                  <>
                    <label className="xe-switch">
                      <input type="checkbox" role="switch" className="review-mute-input" checked={draft.bodyIsLoad}
                             onChange={(e) => set("bodyIsLoad", e.target.checked)} />
                      <span className="review-mute-track" aria-hidden="true" />
                      <span>The body is part of the load</span>
                    </label>
                    <p className="xe-hint">{BODY_IS_LOAD_HINT}</p>
                  </>
                )}
              </fieldset>
            )}
          </details>

          {/* EQUIPMENT — chosen by label, stored by key; the grouped panel with the phone's icons and his own pieces. */}
          <section className="xe-row" aria-labelledby="xe-equipment">
            <h3 className="xe-label" id="xe-equipment">Equipment</h3>
            <div className="xb-anchor" data-pop-anchor="equipment" onKeyDown={escapeCloses}>
              <div className="xb-select xb-select-tags" aria-expanded={pop === "equipment"}>
                {equipment.map((k) => (
                  <span key={k} className="xb-tag">
                    <PieceIcon equipmentKey={k} iconPath={equipmentIconPath(k)} />
                    {equipmentName(k, equipmentSettings)}
                    {COACH_SEES_ENGINE && EQUIPMENT_LABEL[k] === undefined && !isCustomKey(k) && (
                      <span className="xb-flag" aria-label="Not a name the engine knows">?</span>
                    )}
                    <button type="button" className="xb-tag-x" aria-label={`Remove ${equipmentName(k, equipmentSettings)}`}
                            onClick={() => toggleEquipment(k)}>
                      <span aria-hidden="true">✕</span>
                    </button>
                  </span>
                ))}
                <button type="button" className="xb-select-trigger" aria-expanded={pop === "equipment"}
                        aria-haspopup="true" aria-label="Add an equipment" onClick={() => toggle("equipment")}>
                  {equipment.length === 0 && <span className="xb-select-empty xb-select-soft">add…</span>}
                  <Chevron />
                </button>
              </div>
              {pop === "equipment" && (
                <div className="xb-pop" role="group" aria-label="Equipment" ref={revealOnOpen}>
                  <div className="xb-pop-head">
                    <input className="xb-input xb-search" type="search" placeholder="Search equipment"
                           aria-label="Search equipment" value={equipmentQuery}
                           onChange={(e) => setEquipmentQuery(e.target.value)} />
                    <p className="xb-pop-count">{equipment.length} selected</p>
                  </div>
                  {equipmentShown.map((section) => (
                    <div key={section.name} className="xb-pop-section" role="group" aria-label={section.name}>
                      <p className="xb-pop-section-name">{section.name}</p>
                      <div className="xb-pills">
                        {section.keys.map((k) => {
                          const on = equipment.includes(k);
                          return (
                            <span key={k} className={on ? "xb-pill xb-pill-fill is-on" : "xb-pill"}>
                              <button type="button" className="xb-pill-main" aria-pressed={on} onClick={() => toggleEquipment(k)}>
                                <PieceIcon equipmentKey={k} iconPath={equipmentIconPath(k)} />
                                {equipmentName(k, equipmentSettings)}
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  {equipmentShown.length === 0 && <p className="xb-pop-hint">Nothing matches.</p>}
                </div>
              )}
            </div>
          </section>

          <section className="xe-row" aria-labelledby="xb-steps-label">
            <h3 className="xe-label" id="xb-steps-label">How to do it</h3>
            <StepsField steps={draft.instructions} labelledBy="xb-steps-label" onChange={(s) => set("instructions", s)} />
          </section>

          {/* DIFFICULTY — 1 to 10 as one row of choices (the swap page ranks by it); pressing the chosen one clears it.
              Engine data, withheld from coaches for now (`COACH_SEES_ENGINE`, his 2026-09-28 "level is gone"): the
              section is not drawn and a stored value is kept exactly as it is. */}
          {COACH_SEES_ENGINE && (
          <section className="xe-row" aria-labelledby="xe-difficulty">
            <h3 className="xe-label" id="xe-difficulty">Difficulty</h3>
            <div className="ours-chips xe-difficulty" role="radiogroup" aria-labelledby="xe-difficulty">
              {DIFFICULTIES.map((n) => (
                <button key={n} type="button" role="radio" className="ours-chip" aria-checked={draft.difficulty === n}
                        aria-pressed={draft.difficulty === n}
                        onClick={() => set("difficulty", draft.difficulty === n ? null : n)}>
                  {n}
                </button>
              ))}
            </div>
            <p className="xe-hint">{draft.difficulty === null ? "Not set — the swap page ranks by it." : "Press it again to clear."}</p>
          </section>
          )}

          {/* 🔴 TIPS ARE RETIRED FOR NEW WORK (his 2026-09-23 question); shown only where the exercise ALREADY holds
              them, so nothing written is hidden. Notes are the coach's own, never shown to a client. */}
          {saved.tips.length > 0 && (
            <label className="xe-row">
              <span className="xe-label">Tips</span>
              <textarea className="xb-input xb-textarea" rows={1} placeholder="Add exercise tips"
                        value={linesToText(draft.tips)} onChange={(e) => set("tips", textToLines(e.target.value))} />
              <span className="xe-hint">Kept because this exercise already has them. New exercises use How to do it and Notes.</span>
            </label>
          )}
          <label className="xe-row">
            <span className="xe-label">Notes</span>
            <textarea className="xb-input xb-textarea xb-filled" rows={1} placeholder="Add a note for yourself"
                      value={draft.notes} onChange={(e) => set("notes", e.target.value)} />
            <span className="xe-hint">For you — never shown to a client.</span>
          </label>
          {COACH_ENGINE_FORM && <EngineQuestions a={engine} kit={draft.equipment} muscles={draft.primaryMuscles} />}

          {((showProblems && problems.length > 0) || saveError !== null) && (
            <div className="xb-problems" ref={problemsRef} role="alert">
              {showProblems && problems.map((p) => <p key={p.field} className="ht-callout exercise-error">{p.message}</p>)}
              {saveError !== null && <p className="ht-callout exercise-error">{saveError}</p>}
            </div>
          )}
        </div>

        {/* RIGHT: the video, then the exercise ON THE CLIENT'S PHONE as they train it. Visible from the first pixel —
            William has no phone to film from, so the media may never be something to scroll to. */}
        <aside className="xe-side" aria-labelledby="xb-media-title">
          <h3 className="xe-label" id="xb-media-title">Video</h3>
          {draft.demoVideoUrl !== null && draft.demoVideoUrl !== "" && !replacing ? (
            <div className="xb-video">
              {/* 🔴 A STORED URL IS NOT ALWAYS A FILE, AND A `<video>` CANNOT PLAY A PAGE — `videoKind` decides. */}
              {videoKind(draft.demoVideoUrl) === "file" ? (
                <video src={draft.demoVideoUrl} controls playsInline className="xb-video-player" />
              ) : (
                <p className="xb-video-link">
                  <LinkIcon />
                  <a className="ht-link" href={draft.demoVideoUrl} target="_blank" rel="noreferrer noopener">Open the linked video</a>
                  <span className="xb-hint"> {linkHost(draft.demoVideoUrl)}</span>
                </p>
              )}
              <div className="xb-video-actions">
                <button type="button" className="ours-text-action" onClick={() => { setPastedLink(""); setReplacing(true); }}>Replace</button>
                <button type="button" className="ours-text-action" onClick={() => set("demoVideoUrl", null)}>Remove</button>
              </div>
            </div>
          ) : (
            <>
              <div className="xb-link-field">
                <LinkIcon />
                <input className="xb-input xb-link-input" type="url" inputMode="url" disabled={upload.busy}
                       placeholder="Vimeo or YouTube link" aria-label="Vimeo or YouTube link" value={pastedLink}
                       onChange={(e) => { setPastedLink(e.target.value); setLinkNote(null); }}
                       onBlur={() => applyPastedLink()}
                       onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPastedLink(); } }} />
              </div>
              {linkNote !== null && <p className="xb-note" aria-live="polite">{linkNote}</p>}
              <label className={"xb-upload" + (upload.busy ? " is-busy" : "")}
                     onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("is-over"); }}
                     onDragLeave={(e) => e.currentTarget.classList.remove("is-over")}
                     onDrop={(e) => {
                       e.preventDefault();
                       e.currentTarget.classList.remove("is-over");
                       const f = e.dataTransfer.files?.[0];
                       if (f !== undefined && !upload.busy) void uploadVideo(f);
                     }}>
                <input className="ht-visually-hidden" type="file" accept="video/mp4,video/quicktime" disabled={upload.busy}
                       onChange={(e) => { const file = e.target.files?.[0]; if (file !== undefined) void uploadVideo(file); }} />
                {/* 🔴 THE RING AROUND THE CAMERA — his point 9: once for the shrink, again from zero for the upload. */}
                <span className="xb-upload-icon">
                  {videoProgress !== null && <ProgressRing fraction={videoProgress.fraction} />}
                  <VideoIcon />
                </span>
                <span className="xb-upload-label" aria-live="polite">
                  {videoProgress !== null ? progressLabel(videoProgress) : upload.busy ? "Uploading…" : "Upload video"}
                </span>
              </label>
              <p className="xb-hint">Accepted: mp4, mov · over 200 MB is shrunk to fit</p>
              {replacing && (
                <button type="button" className="ours-text-action xb-keep" onClick={() => setReplacing(false)}>
                  Keep the current video
                </button>
              )}
            </>
          )}
          {upload.refusal !== null && <p className="ht-callout exercise-error" role="alert">{upload.refusal}</p>}

          <h3 className="xe-label xe-phone-label">On the client&rsquo;s phone</h3>
          <PhoneExercisePreview name={draft.name} trackingFields={fields}
                                hasVideo={draft.demoVideoUrl !== null && draft.demoVideoUrl !== ""} />
        </aside>
      </div>
    </div>,
  );
}

/* THE MUSCLE STAR — drawn, not a glyph, so its fill and outline are both ours to set (his word,
   2026-09-23): the PRIMARY is a solid gold star; a SECONDARY is the chip's own colour with a white
   outline. The colours live in `direction.css` on `.xb-star` / `.xb-star.is-primary`. */
function StarIcon() {
  return (
    <Icon name="star" className="xb-star-icon" size={14} stroke={1.8} />
  );
}

/* ── THE STEPS ── one field per step, its number a LABEL beside it (the phone's form, and its
   reason: the saved text stays clean prose and renumbering is arithmetic). Empty, it is one box
   saying what goes there; a click starts step 1. Enter starts the next step; Backspace at the start
   of a step takes the number, the dot and the space away as one character; a pasted list becomes
   steps. A step between steps reads as its own line — the gap is the "one line of space" he asked
   for, so the instructions read as steps rather than a block of text. Where the caret goes after
   each edit is decided by the tested rules in `coachExercise`; this only puts it there. */
function StepsField({ steps, labelledBy, onChange }: {
  steps: string[];
  labelledBy: string;
  onChange: (steps: string[]) => void;
}) {
  const refs = useRef<(HTMLTextAreaElement | null)[]>([]);
  const pending = useRef<StepCaret | null>(null);
  // Every field grows to its text, and a pending caret lands after the DOM holds the new steps.
  useLayoutEffect(() => {
    for (const el of refs.current) {
      if (el === null) continue;
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
    const c = pending.current;
    if (c === null) return;
    pending.current = null;
    const el = refs.current[c.index];
    if (el !== null && el !== undefined) { el.focus(); el.setSelectionRange(c.offset, c.offset); }
  });
  function apply(next: string[], caret: StepCaret | null) {
    pending.current = caret;
    onChange(next);
  }

  if (steps.length === 0) {
    return (
      <button type="button" className="xb-steps xb-steps-empty" aria-labelledby={labelledBy}
              onClick={() => apply([""], { index: 0, offset: 0 })}>
        Add exercise instructions
      </button>
    );
  }
  return (
    <ol className="xb-steps" aria-labelledby={labelledBy}>
      {steps.map((text, i) => (
        <li key={i} className="xb-step" onPointerDown={(e) => {
          // A press on the number or the gap beside it puts the caret in the step, as the phone's row does.
          if (e.target === e.currentTarget || (e.target as Element).classList.contains("xb-step-n")) {
            e.preventDefault();
            refs.current[i]?.focus();
          }
        }}>
          <span className="xb-step-n" aria-hidden="true">{i + 1}.</span>
          <textarea ref={(el) => { refs.current[i] = el; }} className="xb-step-text" rows={1}
                    aria-label={`Step ${i + 1}`} value={text}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v.includes("\n")) { const r = pasteSteps(steps, i, 0, text.length, v); apply(r.steps, r.caret); return; }
                      onChange(steps.map((s, j) => (j === i ? v : s)));
                    }}
                    onPaste={(e) => {
                      const pasted = e.clipboardData.getData("text/plain");
                      if (!/\r?\n/.test(pasted)) return;
                      e.preventDefault();
                      const el = e.currentTarget;
                      const r = pasteSteps(steps, i, el.selectionStart, el.selectionEnd, pasted);
                      apply(r.steps, r.caret);
                    }}
                    onKeyDown={(e) => {
                      const el = e.currentTarget;
                      const start = el.selectionStart, end = el.selectionEnd;
                      if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        const cut = steps.map((s, j) => (j === i ? text.slice(0, start) + text.slice(end) : s));
                        const r = splitStep(cut, i, start);
                        apply(r.steps, r.caret);
                      } else if (e.key === "Backspace" && start === 0 && end === 0) {
                        if (i === 0 && text !== "") return;
                        e.preventDefault();
                        const r = backspaceStepStart(steps, i);
                        apply(r.steps, r.caret);
                      } else if (e.key === "ArrowUp" && start === 0 && end === 0 && i > 0) {
                        e.preventDefault();
                        const above = refs.current[i - 1];
                        above?.focus(); above?.setSelectionRange(above.value.length, above.value.length);
                      } else if (e.key === "ArrowDown" && start === text.length && i < steps.length - 1) {
                        e.preventDefault();
                        const below = refs.current[i + 1];
                        below?.focus(); below?.setSelectionRange(0, 0);
                      }
                    }} />
        </li>
      ))}
    </ol>
  );
}

/** A thin ring drawn on the icon's own circle, filling clockwise from twelve o'clock. */
function ProgressRing({ fraction }: { fraction: number }) {
  const r = 14, c = 2 * Math.PI * r;
  const f = Math.min(1, Math.max(0, fraction));
  return (
    <svg className="xb-ring" viewBox="0 0 32 32" aria-hidden="true">
      <circle className="xb-ring-track" cx="16" cy="16" r={r} />
      <circle className="xb-ring-fill" cx="16" cy="16" r={r}
              strokeDasharray={c} strokeDashoffset={c * (1 - f)} />
    </svg>
  );
}

function LinkIcon() {
  return (
    <Icon name="link" className="xb-link-icon" size={14} />
  );
}

function VideoIcon() {
  return (
    <Icon name="video" size={16} />
  );
}
